import { AbstractControl, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { cloneDeep, get, isEqual, set, unset } from 'lodash';
import { ChangeEntry, ChangeKind } from './change-review.model';

// undo for a change whose segments mirror the form below root; null when it can't be undone generically
export function revertAt(root: AbstractControl, tokens: string[], entry: ChangeEntry): (() => void) | null {
  const segments = tokens.map((token) => (token.startsWith('[') ? token.slice(1, -1) : token));
  let control = root;
  let depth = 0;
  while (depth < segments.length && !(control instanceof UntypedFormControl)) {
    const next = segments[depth] === '' ? null : control.get([segments[depth]]);
    if (!next) break;
    control = next;
    depth++;
  }

  const remaining = segments.slice(depth);
  if (!remaining.length) {
    const parent = control.parent;
    if (parent instanceof UntypedFormArray && entry.kind !== ChangeKind.Changed) {
      // a removed list item can't be rebuilt generically
      return entry.kind === ChangeKind.Added ? () => parent.removeAt(parent.controls.indexOf(control)) : null;
    }
    return () =>
      control instanceof UntypedFormControl
        ? control.setValue(entry.before ?? null)
        : control.patchValue(entry.before ?? {});
  }
  // below a plain control, edit inside its value
  if (!(control instanceof UntypedFormControl)) return null;

  return () => control.setValue(revertedValue(cloneDeep(control.value), remaining, entry));
}

function revertedValue(value: unknown, segments: string[], entry: ChangeEntry): unknown {
  if (segments[segments.length - 1] === '') {
    const listPath = segments.slice(0, -1);
    const list: unknown[] = (listPath.length ? get(value, listPath) : value) ?? [];
    const reverted =
      entry.kind === ChangeKind.Added ? list.filter((item) => !isEqual(item, entry.after)) : [...list, entry.before];
    if (!listPath.length) return reverted;
    return set((value ?? {}) as object, listPath, reverted);
  }
  const target = (value ?? {}) as object;
  if (entry.kind === ChangeKind.Added) {
    unset(target, segments);
  } else {
    set(target, segments, entry.before);
  }
  return target;
}
